import React from 'react';

const SearchSuggestions = ({ suggestions, onSelect, query }) => {
  // Highlight matching text in suggestions
  const highlightMatch = (text, query) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-blue-100 text-blue-800 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Group suggestions by type
  const groupedSuggestions = suggestions.reduce((acc, suggestion) => {
    const category = suggestion.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(suggestion);
    return acc;
  }, {});

  const categoryLabels = {
    doctors: '👨‍⚕️ Doctors',
    specialties: '🏥 Specialties', 
    conditions: '🩺 Conditions',
    providers: '🏢 Healthcare Providers',
    locations: '📍 Locations',
    general: '🔍 Suggestions'
  };

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
      {Object.entries(groupedSuggestions).map(([category, items]) => (
        <div key={category} className="border-b border-gray-100 last:border-b-0">
          {/* Category Header */}
          <div className="px-4 py-2 bg-gray-50 text-sm font-medium text-gray-600 sticky top-0">
            {categoryLabels[category] || category}
          </div>
          
          {/* Suggestions */}
          {items.map((suggestion, index) => (
            <button
              key={`${category}-${index}`}
              onClick={() => onSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700 focus:outline-none transition-colors duration-150 flex items-center justify-between group"
            >
              <div className="flex items-center space-x-3">
                {/* Suggestion Icon */}
                <div className="flex-shrink-0">
                  {suggestion.type === 'doctor' && (
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm">👨‍⚕️</span>
                    </div>
                  )}
                  {suggestion.type === 'specialty' && (
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">🏥</span>
                    </div>
                  )}
                  {suggestion.type === 'provider' && (
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 text-sm">🏢</span>
                    </div>
                  )}
                  {suggestion.type === 'condition' && (
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 text-sm">🩺</span>
                    </div>
                  )}
                  {!suggestion.type && (
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 text-sm">🔍</span>
                    </div>
                  )}
                </div>
                
                {/* Suggestion Text */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">
                    {highlightMatch(suggestion.text, query)}
                  </div>
                  {suggestion.subtitle && (
                    <div className="text-sm text-gray-500 truncate">
                      {suggestion.subtitle}
                    </div>
                  )}
                </div>
              </div>

              {/* Result Count */}
              {suggestion.count && (
                <div className="flex-shrink-0 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full group-hover:bg-blue-100 group-hover:text-blue-600">
                  {suggestion.count} results
                </div>
              )}
            </button>
          ))}
        </div>
      ))}
      
      {/* No suggestions message */}
      {suggestions.length === 0 && (
        <div className="px-4 py-8 text-center text-gray-500">
          <div className="mb-2">🔍</div>
          <div>No suggestions found</div>
          <div className="text-xs text-gray-400 mt-1">
            Try different keywords or check spelling
          </div>
        </div>
      )}
      
      {/* Footer with tip */}
      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center border-t">
        💡 Tip: Use keywords like "cardiologist near me" or "urgent care downtown"
      </div>
    </div>
  );
};

export default SearchSuggestions;